import { useParams, useNavigate } from 'react-router';
import { useTrainee, useProductionLogs } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
  ArrowLeft,
  Edit,
  Loader2,
  Mail,
  Calendar,
} from '@t90/ui';
import { formatDate, formatNumber, formatPhone } from '@t90/common';

export function TraineeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: trainee, isLoading, error } = useTrainee(id!);
  // TODO: Replace with correct assignmentId string for this trainee when available
  const { data: productionLogs } = useProductionLogs(undefined);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !trainee) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-lg font-medium">Trainee not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/trainees')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Trainees
        </Button>
      </div>
    );
  }

  const initials = `${trainee.first_name?.[0] ?? ''}${trainee.last_name?.[0] ?? ''}`;
  const totalUnits = productionLogs?.reduce((sum, log) => sum + (log.units_completed ?? 0), 0) ?? 0;
  const totalHours = productionLogs?.reduce((sum, log) => sum + (log.hours_worked ?? 0), 0) ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trainees')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Avatar className="h-16 w-16">
            <AvatarImage src={trainee.avatar_url ?? undefined} />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">
                {trainee.first_name} {trainee.last_name}
              </h1>
              <Badge variant={getStatusVariant(trainee.status)}>{trainee.status}</Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              {trainee.email && (
                <span className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  {trainee.email}
                </span>
              )}
              {trainee.cohort && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Cohort {trainee.cohort}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/trainees/${id}/edit`)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatNumber(totalUnits)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totalHours.toFixed(1)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Units/Hour</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {totalHours > 0 ? (totalUnits / totalHours).toFixed(1) : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="production">Production</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow label="Full Name" value={`${trainee.first_name} ${trainee.last_name}`} />
                <InfoRow label="Email" value={trainee.email ?? '-'} />
                <InfoRow label="Phone" value={trainee.phone ? formatPhone(trainee.phone) : '-'} />
                <InfoRow label="Location" value={trainee.location_id ?? '-'} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Program Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow label="Status" value={trainee.status} />
                <InfoRow label="Cohort" value={trainee.cohort ?? '-'} />
                <InfoRow label="Start Date" value={trainee.start_date ? formatDate(trainee.start_date) : '-'} />
                <InfoRow label="End Date" value={trainee.graduation_target_date ? formatDate(trainee.graduation_target_date) : '-'} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="production" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Production</CardTitle>
              <CardDescription>Production logs for this trainee</CardDescription>
            </CardHeader>
            <CardContent>
              {productionLogs?.length ? (
                <div className="space-y-3">
                  {productionLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div>
                        <p className="font-medium">{formatDate(log.log_date)}</p>
                        <p className="text-sm text-muted-foreground">
                          {log.project_id ? `Project ${log.project_id.substring(0, 8)}...` : 'Unknown Project'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatNumber(log.units_completed ?? 0)} units</p>
                        <p className="text-xs text-muted-foreground">
                          {log.hours_worked?.toFixed(1) ?? '-'} hrs
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No production logs recorded yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {trainee.notes ? (
                <p className="text-sm whitespace-pre-wrap">{trainee.notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No notes added yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Graduated':
      return 'default';
    case 'On Leave':
      return 'warning';
    case 'Terminated':
      return 'destructive';
    case 'Pending':
      return 'secondary';
    default:
      return 'secondary';
  }
}
